import { Config } from 'config/envConfig';
import React, { forwardRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@quyva/shared-ui';
import {
    faBrain,
    faComments,
    faChartLine,
    faRobot,
    faDatabase,
    faCalendarAlt,
    faGlobe,
    faShieldAlt,
    faCogs,
} from '@fortawesome/free-solid-svg-icons';
import { appName } from './Constants';
import ScheduleDemoModal from './ScheduleDemoModal';
import TryBotModal from './TryBotModal';

const Layout = forwardRef((props, ref) => {
    const useSecuredConnection = Config.USE_SECURED_CONNECTION;
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [showTryModal, setShowTryModal] = useState(false);

    const features = [
        { icon: faComments, label: 'Chat' },
        { icon: faDatabase, label: 'Data Extraction' },
        { icon: faRobot, label: 'Agents' },
        { icon: faChartLine, label: 'Reporting' },
    ];

    return (
        <div ref={ref}>
            {/* Hero Section */}
            <section className='pt-32 pb-20 bg-gradient-to-br from-landing-slate-900 to-landing-slate-800 relative overflow-hidden'>
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className='max-w-7xl mx-auto px-6 relative z-10'>
                    <div className='flex flex-col lg:flex-row items-center gap-12'>
                        <div className='flex-1 text-center lg:text-left'>
                            <div className='inline-flex items-center gap-2 bg-landing-blue-500/10 border border-landing-blue-500/20 rounded-full px-4 py-2 mb-6'>
                                <FontAwesomeIcon
                                    icon={faBrain}
                                    className='text-landing-blue-400'
                                />
                                <span className='text-landing-blue-300 text-sm font-medium'>
                                    AI-Powered Enterprise Platform
                                </span>
                            </div>
                            <h1 className='text-4xl md:text-5xl lg:text-6xl font-bold text-landing-white mb-6 leading-tight'>
                                {appName}
                            </h1>
                            <p className='text-lg text-landing-slate-300 mb-8 max-w-2xl'>
                                Engage with your data — documents, images, and
                                databases — using natural language. Quyva
                                integrates LLMs, knowledge graphs, and
                                automation to deliver actionable intelligence.
                            </p>
                            {/* Feature Pills */}
                            <div className='flex flex-wrap gap-3 justify-center lg:justify-start mb-8'>
                                {features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className='flex items-center gap-2 bg-landing-blue-500/10 border border-landing-blue-500/20 rounded-full px-4 py-2'
                                    >
                                        <FontAwesomeIcon
                                            icon={feature.icon}
                                            className='text-landing-blue-400 text-sm'
                                        />
                                        <span className='text-landing-slate-300 text-sm'>
                                            {feature.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <p className='text-lg text-landing-slate-300 mb-8 max-w-2xl'>
                                Experience Quyva in action — schedule a demo or try the AI sales agent on your website instantly.
                            </p>
                            <div className='flex flex-col sm:flex-row gap-4 justify-center lg:justify-start'>

                                <Button
                                    text="Schedule Demo"
                                    icon={faCalendarAlt}
                                    onClick={() => setShowDemoModal(true)}
                                    className="!bg-backgrounds-primary300 !text-texts-primary hover:!bg-backgrounds-primary400 border-none px-8 py-4 shadow-sm flex items-center justify-center gap-1"
                                />

                                <Button
                                    text="Try Sales Agent"
                                    icon={faRobot}
                                    onClick={() => setShowTryModal(true)}
                                    className="!bg-backgrounds-primary300 !text-texts-primary hover:!bg-backgrounds-primary400 border-none px-8 py-4 shadow-sm flex items-center justify-center gap-1"
                                />
                            </div>
                        </div>
                        <div className='flex-1 flex justify-center items-center'>
                            <div className='relative'>
                                <div className='absolute -inset-4 bg-landing-blue-500/20 rounded-2xl blur-xl'></div>
                                <img
                                    src={`${Config.PUBLIC_URL}/original.png`}
                                    alt='Quyva Platform'
                                    className='relative rounded-2xl w-full max-w-lg shadow-2xl border border-landing-slate-700'
                                    draggable='false'
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `${Config.PUBLIC_URL}/QuyvaLogo.png`;
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Features Section */}
            <section className='py-16 bg-gradient-to-r from-landing-slate-50 to-landing-blue-50 border-b border-landing-gray-100'>
                <div className='max-w-7xl mx-auto px-6'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6'>
                        <div className='text-center bg-landing-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow'>
                            <div className='w-14 h-14 bg-landing-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <FontAwesomeIcon
                                    icon={faDatabase}
                                    className='text-landing-purple-600 text-xl'
                                />
                            </div>
                            <div className='text-lg font-bold text-landing-slate-900 mb-2'>
                                Unified Knowledge
                            </div>
                            <div className='text-landing-slate-600 text-xs'>
                                Docs • Images • Databases • Web
                            </div>
                        </div>
                        <div className='text-center bg-landing-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow'>
                            <div className='w-14 h-14 bg-landing-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <FontAwesomeIcon
                                    icon={faBrain}
                                    className='text-landing-blue-600 text-xl'
                                />
                            </div>
                            <div className='text-lg font-bold text-landing-slate-900 mb-2'>
                                Precision AI
                            </div>
                            <div className='text-landing-slate-600 text-xs'>
                                Custom RAG • Knowledge Graphs
                            </div>
                        </div>
                        <div className='text-center bg-landing-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow'>
                            <div className='w-14 h-14 bg-landing-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <FontAwesomeIcon
                                    icon={faCogs}
                                    className='text-landing-emerald-600 text-xl'
                                />
                            </div>
                            <div className='text-lg font-bold text-landing-slate-900 mb-2'>
                                AI Automation
                            </div>
                            <div className='text-landing-slate-600 text-xs'>
                                Automate Real Work
                            </div>
                        </div>
                        <div className='text-center bg-landing-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow'>
                            <div className='w-14 h-14 bg-landing-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <FontAwesomeIcon
                                    icon={faGlobe}
                                    className='text-landing-orange-600 text-xl'
                                />
                            </div>
                            <div className='text-lg font-bold text-landing-slate-900 mb-2'>
                                Engage Anywhere
                            </div>
                            <div className='text-landing-slate-600 text-xs'>
                                Web • Chat • Email • Multilingual
                            </div>
                        </div>
                        <div className='text-center bg-landing-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow'>
                            <div className='w-14 h-14 bg-landing-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <FontAwesomeIcon
                                    icon={faShieldAlt}
                                    className='text-landing-red-600 text-xl'
                                />
                            </div>
                            <div className='text-lg font-bold text-landing-slate-900 mb-2'>
                                Enterprise Control
                            </div>
                            <div className='text-landing-slate-600 text-xs'>
                                Fine-grained Access • Cloud / On-Prem
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Schedule Demo Modal */}
            <ScheduleDemoModal
                isOpen={showDemoModal}
                onClose={() => setShowDemoModal(false)}
            />

            {/* Try Bot Modal */}
            <TryBotModal
                isOpen={showTryModal}
                onClose={() => setShowTryModal(false)}
            />
        </div>
    );
});

export default Layout;
